import { vatNote } from '../config/vat';
// Cloud PBX page content. Based on the CLI Secure Cloud Hosted PBX page (same platform), in VOIP2 branding.
const M = '/media/pbx';

export const CLOUD_PBX = {
  hero: {
    title: 'Your phone system,',
    highlight: 'in the cloud',
    sub: 'Replace the office switchboard with a phone system in the cloud. HD calls, smart routing and any device, with no hardware to buy.',
    image: `${M}/hero-devices.webp`,
    bullets: ['14-day free trial', 'Simple 24-month plans', 'Next-day setup', 'Keep your numbers'],
  },

  trust: [
    { value: '250+', label: 'CRM integrations' },
    { value: '140+', label: 'countries for numbers' },
    { value: '99.99%', label: 'uptime SLA' },
    { value: '24/7', label: 'UK-based support' },
  ],

  intro: {
    title: 'Move your phone lines to the cloud',
    paragraphs: [
      'A cloud PBX puts your whole phone system online, so there is no on-site hardware to buy, house or maintain. Make and take business calls from a desk phone, the desktop app or your mobile, with one business number wherever your team works.',
      'You get HD voice, call routing, auto-attendants, call recording and reporting from one portal. Add or remove users in minutes and pay only for the seats you use.',
    ],
    bullets: ['No on-site PBX hardware', 'Work from any device', 'Keep or port your numbers', 'Scale up or down in minutes'],
  },

  platform: {
    title: 'Every phone-system control in one portal',
    sub: 'Manage users, numbers, queues and call flows from the VOIP2 portal. Click a screen for a closer look.',
    screens: [
      { img: `${M}/screen-routing.webp`, title: 'Call routing', body: 'Answering rules and time frames for every number: business hours, out of hours and holidays.' },
      { img: `${M}/screen-users.webp`, title: 'Users and extensions', body: 'Add team members, assign roles and extensions, and choose which devices they use.' },
      { img: `${M}/screen-queues.webp`, title: 'Call queues', body: 'Live wallboard with callers waiting, wait times and every agent’s status.' },
      { img: `${M}/screen-ivr.webp`, title: 'Auto-attendant (IVR)', body: 'Build menus visually and send callers to the right team first time.' },
      { img: `${M}/screen-analytics.webp`, title: 'Call history and reporting', body: 'Filter every call, play recordings and track volumes by day and hour.' },
      { img: `${M}/screen-contacts.webp`, title: 'Contacts and CRM', body: 'Click-to-dial from your contacts, with calls logged to your CRM automatically.' },
    ],
  },

  pricing: {
    title: 'Simple per-user pricing',
    sub: `A 14-day free trial on every plan. Prices per user per month on a 24-month term, ${vatNote()}. Shorter terms are available at a higher price.`,
    note: 'UK landline and mobile minutes are included per user as shown. The free trial includes 100 UK minutes in total. Your first number is included; extra numbers are £2 per month. Volume discount: 5% from 10 users, 10% from 25 and 15% from 40; over 50 users we will send you a quote. International bundles and numbers in 140+ countries are available on request.',
    plans: [
      {
        name: 'Standard', tagline: 'Everything a small team needs to get calling.', monthly: 10,
        features: ['500 UK landline & mobile minutes per user', 'VoIP phone line', 'Mobile apps for iOS & Android', 'Web phone for PC & Mac', 'Self-care portal', 'Voicemail to email', 'Call forwarding'],
      },
      {
        name: 'Business', tagline: 'Unlimited UK calls for busy teams.', monthly: 16, highlight: true,
        features: ['Unlimited UK landline & mobile calls (fair use 2,500 minutes per user)', 'Automatic call recording', 'Click-to-dial', 'Custom roles & permissions', 'Team presence & messaging', 'Multiple sites', 'SMS from 4p per message'],
      },
    ],
  },

  // Shown under the plan cards, like the "included in every package" strip on comparable UK provider pages.
  included: ['Keep your numbers', 'Next-day setup', '24-month price lock', '14-day free trial', 'Apps for every device', '24/7 UK-based support'],

  // Full plan comparison. Built only from the features listed on the plan cards above; each plan includes the one before it.
  // Values: true = included, false = not included, string = shown as text.
  compare: {
    title: 'Compare every feature',
    sub: `Each plan includes everything in the plan before it. Prices ${vatNote()}.`,
    plans: ['Standard', 'Business'],
    groups: [
      {
        title: 'Calls & numbers',
        rows: [
          ['UK landline & mobile minutes per user', '500', 'Unlimited (fair use 2,500)'],
          ['Keep or port your numbers', true, true],
          ['Numbers in 140+ countries', 'On request', 'On request'],
          ['International call bundles', 'On request', 'On request'],
          ['SMS', false, 'From 4p'],
          ['Multiple sites', false, true],
          ['Free calls between your users', true, true],
          ['Choose your outbound caller ID', true, true],
          ['Block unwanted callers', true, true],
          ['Fax to email', 'On request', 'On request'],
        ],
      },
      {
        title: 'Call handling',
        rows: [
          ['Voicemail to email', true, true],
          ['Call forwarding', true, true],
          ['Auto-attendant (IVR)', true, true],
          ['Ring groups & call queues', true, true],
          ['Business hours & holiday routing', true, true],
          ['Hold music & custom greetings', true, true],
          ['Transfer, hold & three-way calling', true, true],
          ['Conference calls', true, true],
          ['Automatic call recording', false, true],
          ['Pause & resume recording', false, true],
          ['Click-to-dial', false, true],
        ],
      },
      {
        title: 'Apps & teamwork',
        rows: [
          ['Mobile apps for iOS & Android', true, true],
          ['Web phone for PC & Mac', true, true],
          ['Desktop app for Windows & macOS', true, true],
          ['Visual voicemail', true, true],
          ['Company directory', true, true],
          ['HD video meetings & screen sharing', true, true],
          ['Single sign-on (Google Workspace & Microsoft 365)', true, true],
          ['Team presence & messaging', false, true],
        ],
      },
      {
        title: 'Admin & reporting',
        rows: [
          ['Self-care portal', true, true],
          ['Call history & missed-call alerts', true, true],
          ['Bulk user import', true, true],
          ['Custom roles & permissions', false, true],
        ],
      },
      {
        title: 'Support',
        rows: [
          ['24/7 UK-based support', true, true],
          ['Next-day setup', true, true],
        ],
      },
    ],
  },

  // Optional extras. No prices here: each is quoted with the plan.
  addons: {
    title: 'Add what you need',
    sub: 'Extend any plan as your business grows. Add-ons are quoted with your plan.',
    items: [
      { icon: 'Bot', title: 'AI Receptionist', body: 'Answer every call 24/7, book appointments and route callers.', to: '/products/ai-receptionist' },
      { icon: 'Globe', title: 'International & toll-free numbers', body: 'Local, national, toll-free and UIFN numbers in 140+ countries.', to: '/phone-numbers/did-international' },
      { icon: 'Wallet', title: 'International call bundles', body: 'Minutes bundles for the countries your team calls most.' },
      { icon: 'Phone', title: 'IP desk phones', body: 'Pre-provisioned Yealink and Grandstream handsets, ready to plug in.' },
      { icon: 'Server', title: 'SIP trunking', body: 'Keep your existing PBX and connect it to the VOIP2 network.', to: '/products/sip-trunking' },
      { icon: 'KeyRound', title: 'Voice OTP & Flash Calls', body: 'Verify users with a missed call instead of an SMS code.', to: '/products/voice-otp' },
    ],
  },

  communicator: {
    title: 'One app for calls, chat and presence',
    sub: 'Use your extension on your mobile or desktop as if it were a desk phone, with team chat, presence, the company directory and visual voicemail in the same app.',
    image: `${M}/softphone.webp`,
    bullets: ['Call control & transfer', 'Caller ID & screen pops', 'Multiple lines', 'Visual voicemail', 'Team chat & presence', 'Windows, macOS, iOS & Android'],
    cards: [
      { icon: 'Globe', title: 'Nothing to install', body: 'The web phone opens in Chrome, Edge, Safari or Firefox. No plugin, no client to deploy.' },
      { icon: 'KeyRound', title: 'Single sign-on', body: 'Sign in with Google Workspace or Microsoft 365.' },
      { icon: 'Lock', title: 'Encrypted calls', body: 'TLS signalling and SRTP media, so calls are never sent in clear text.' },
      { icon: 'Repeat', title: 'Switch device mid-call', body: 'Move a live call from your desk to your mobile in a tap. The caller hears nothing.' },
    ],
  },

  video: {
    title: 'HD video meetings, built in',
    sub: 'Run stand-ups and customer meetings without another tool: same login, same app, same contacts.',
    image: `${M}/video-meeting.webp`,
    features: [
      { icon: 'Video', title: 'HD video & audio', body: 'Up to 1080p video and HD audio with echo cancellation.' },
      { icon: 'MonitorUp', title: 'Screen sharing', body: 'Share a window, a tab or your full screen.' },
      { icon: 'Users', title: 'Up to 100 participants', body: 'From quick huddles to all-hands meetings.' },
      { icon: 'Captions', title: 'Live captions', body: 'Captions and recording transcripts in 30+ languages.' },
      { icon: 'Hand', title: 'Reactions & raise hand', body: 'Keep meetings interactive with reactions and Q&A.' },
      { icon: 'CalendarDays', title: 'Calendar sync', body: 'Join in one click from Outlook and Google Calendar.' },
    ],
  },

  numbers: {
    title: 'Bring your number, or choose a new one',
    sub: 'Pick a memorable UK number or one from 140+ countries, or port the numbers you already have with no downtime.',
    steps: [
      { verb: 'Choose', body: 'Pick a UK or international number, or keep your own.' },
      { verb: 'Pick a plan', body: 'Match the plan to your team size and call volume.' },
      { verb: 'Provision', body: 'We set everything up in the cloud. No engineer visit.' },
      { verb: 'Go live', body: 'Start taking calls straight away.' },
    ],
  },

  devices: {
    title: 'Use any device, anywhere',
    items: [
      { icon: 'Smartphone', title: 'Mobile app', body: 'Your office extension on iOS or Android.' },
      { icon: 'Laptop', title: 'Web phone', body: 'Make and receive calls in your browser.' },
      { icon: 'Phone', title: 'IP desk phones', body: 'Pre-provisioned Yealink and Grandstream handsets.' },
      { icon: 'Video', title: 'Video & chat', body: 'Meetings, screen share and messaging in one app.' },
    ],
  },

  reliability: {
    title: 'A platform built not to fall over',
    sub: 'The platform runs active-active across three Tier-4 data centres. If a node fails, calls fail over with no loss of configuration.',
    dcs: [
      { city: 'London', region: 'Primary UK', body: 'Equinix campus with resilient 10 Gb interconnects.' },
      { city: 'Amsterdam', region: 'Europe', body: 'Active cluster for European traffic, seconds away from London.' },
      { city: 'Las Vegas', region: 'Americas', body: 'Third active node for round-the-clock resilience.' },
    ],
    stats: [
      { value: '99.99%', label: 'uptime SLA' },
      { value: '3', label: 'Tier-4 data centres' },
      { value: '<50 ms', label: 'voice latency' },
    ],
  },

  crm: {
    title: 'Works with the tools you already use',
    sub: 'Sync contacts, log calls automatically and trigger workflows in 250+ CRM and business apps.',
    logos: ['sage', 'hubspot', 'teams', 'xero', 'quickbooks', 'zoho', 'bullhorn', 'bitrix24'].map((n) => ({ src: `${M}/crm/${n}.webp`, alt: n })),
  },

  faqs: [
    { q: 'How much does a cloud PBX cost?', a: `Standard is £10 per user per month with 500 UK minutes per user, and Business is £16 with unlimited UK landline and mobile calls (fair use 2,500 minutes per user), ${vatNote()}. Your first number is included and extra numbers are £2 a month. Prices are for a 24-month term (12-month and monthly terms cost a little more), and every plan starts with a 14-day free trial with 100 UK minutes.` },
    { q: 'What do I need to get started?', a: 'A broadband or leased-line connection. You can start with the free desktop and mobile apps straight away, or we can supply pre-provisioned IP desk phones. We handle number porting and set everything up, usually by the next working day.' },
    { q: 'Do I need special phones?', a: 'No. Use the apps on any computer or mobile, or the web phone in your browser. If you prefer handsets, we supply plug-and-play Yealink and Grandstream IP phones.' },
    { q: 'Can I keep my existing numbers?', a: 'Yes. We port your geographic, non-geographic and 0800 numbers with no loss of service, and can add new numbers in the UK or 140+ countries.' },
    { q: 'Is call quality good enough for business?', a: 'Yes. Calls use HD codecs over a carrier-grade network with a 99.99% uptime SLA across three Tier-4 data centres.' },
    { q: 'Is there a long-term contract?', a: 'No. Plans are per-user licences billed monthly. Add or remove users as you grow.' },
  ],

  userBands: ['1–5 users', '6–15 users', '16–50 users', '51–100 users', '100+ users'],

  related: [
    { label: 'SIP Trunking', to: '/products/sip-trunking' },
    { label: 'DID International Numbers', to: '/phone-numbers/did-international' },
    { label: 'AI Receptionist', to: '/products/ai-receptionist' },
  ],
};
